import * as React from 'react';
import { observer } from 'mobx-react';
import injectSheet, { JSSClassesProps } from 'react-jss';
import { debounce } from 'lodash';

import { HotelTitle, TitleProps } from './HotelTitle';

import { HotelSubtitle } from './HotelSubtitle';
import { HotelPhotoGallery } from './HotelPhotoGallery';
import { HotelRoomFacilities } from './HotelRoomFacilities';
import { HotelOnMap } from './HotelOnMap';

import { AdaptiveType } from '../../models/adaptive';

import {
    HOTEL_ROOM_FACILITIES_ID,
    HOTEL_REVIEWS_LIST_ELEM_ID,
    HOTEL_MAP_ELEM_ID,
    HOTEL_BASIC_INFO_ID,
    ROOMS_ID,
} from '../../consts/selectors';

import {
    getAdaptiveType,
    getSearchFormAdaptiveType,
    getPostFiltersAdaptiveType,
    getScrollWidth,
    getHotelRoomsAdaptiveType,
} from '../../utils/adaptive';
import { getReviewsAdaptiveType } from '../../utils/reviews';
import S from '../../stores';

import { hotelTitleStyles, ComponentJSSClasses } from './styles';
import { canUseDOM } from 'sletat-common-utils/lib/canUseDOM';
import { HotelPageModel } from 'sletat-api-services/lib/sletat/models/initial-state/page-models/hotel';
import { InitialState } from 'sletat-api-services/lib/sletat/models/initial-state/index';
import { NavigationMenu } from './NavigationMenu';
import { NavigationMenuItem } from './NavigationMenu/NavigationMenu';
import { HotelReviews } from './HotelReviews';
import { HotelInfo } from './HotelInfo';
import { getActionsParams } from '../../../utils/getActionsParams';
import { anchor } from '../../../utils/anchor';
import classNames from 'classnames';
import { HotelRooms } from './HotelRooms';
import { KnownVisitSources } from '../../../consts/knownVisitSources';

const HotelTitleBlock = injectSheet<TitleProps>(hotelTitleStyles)(HotelTitle);

export interface HotelPageMetaProps {
    previousSource: KnownVisitSources | null;
}

export interface HotelPageProps {
    initialState?: InitialState<HotelPageModel>;
    hotelRooms?: InitialState<HotelPageModel>;
    additionalProps?: HotelPageMetaProps;
}

interface HotelPageState { }

interface StyledHotelProps
    extends JSSClassesProps<ComponentJSSClasses>,
    HotelPageProps { }

const DEFAULT_NAVIGATION_ITEMS: Array<NavigationMenuItem> = [
    {
        title: 'Номера',
        blockId: ROOMS_ID,
    },
    {
        title: 'Услуги',
        blockId: HOTEL_BASIC_INFO_ID,
    },
    {
        title: 'На карте',
        blockId: HOTEL_MAP_ELEM_ID,
    },
    {
        title: 'Отзывы',
        blockId: HOTEL_REVIEWS_LIST_ELEM_ID,
    }
];


@observer
export class HotelPage extends React.Component<StyledHotelProps, HotelPageState> {

    constructor(props: StyledHotelProps) {
        super(props);

        if (isSSR) {
            S.setSsrInitialState(props.initialState!, props.additionalProps);
        }
    }

    componentDidMount() {
        if (canUseDOM) {
            const actions = getActionsParams(location.href);
            if (actions?.scrollTo) anchor(actions.scrollTo);
            window.addEventListener('resize', this.resizeHandler);
            S.searchForm.initSearchForm();
            S.startSearch();
        }
    }

    componentWillUnmount() {
        if (canUseDOM) {
            window.removeEventListener('resize', this.resizeHandler);
            S.tours.stopSearch();
        }
    }

    render() {
        const { classes } = this.props;
        return (
            <main className={classes.container}>
                <section
                    className={classNames(
                        classes.mainLayout,
                        classes.mainLayoutHeader
                    )}
                >
                    <header className={classes.titleBlock}>
                        <div className={classes.breadcrumbsAndNameContainer}>
                            <HotelTitleBlock
                                categoryName={S.hotelDetails.categoryName || ''}
                                hotelName={S.hotelDetails.hotelName || ''}
                            />
                        </div>

                        <HotelSubtitle
                            todayViewsCount={S.hotelDetails.hotelViewsToday}
                            popularityLevel={S.hotelDetails.popularityLevel}
                            rating={S.hotelDetails.rate}
                            reviewAmount={S.hotelReviews.totalReviewAmount}
                            countryName={S.hotelDetails.countryName}
                            resortName={S.hotelDetails.resortName}
                        />
                    </header>
                    <HotelPhotoGallery
                        isMobileView={
                            S.ui.adaptiveType !== AdaptiveType.MoreThan1024
                        }
                        hotelId={S.hotelDetails.hotelId}
                        resortId={S.hotelDetails.resortId}
                        hotelName={S.hotelDetails.hotelName}
                        hotelcategoryName={S.hotelDetails.categoryName}
                        hotelImagesCount={S.hotelDetails.hotelImagesCount}
                        hotelBeachSandType={S.hotelDetails.beachSandType}
                        hotelLatitude={S.hotelDetails.hotelLatitude}
                        hotelLongitude={S.hotelDetails.hotelLongitude}
                    />
                </section>
                <section className={classNames(classes.mainLayout, classes.mainLayoutMod)}>
                    <HotelRoomFacilities
                        id={HOTEL_ROOM_FACILITIES_ID}
                        hotelCommonFacilities={S.hotelDetails.hotelCommonFacilities.map(
                            (item) => item.name
                        )}
                        hotelMealVariants={S.hotelDetails.hotelMealsFacilities.map(
                            (item) => item.name
                        )}
                    />
                </section>

                <NavigationMenu items={this.getActualNavLinks()} />

                {/* <SearchForm /> */}

                {/* <section id={TOUR_LIST_ELEM_ID} className={classes.dark}>
                    <div className={classes.mainLayout}>
                        <SearchResults />
                    </div>
                </section> */}

                <section
                    id={ROOMS_ID}
                    className={classes.mainLayout}
                >
                    <HotelRooms adaptiveType={S.ui.hotelRoomsAdaptiveType} rooms={S.hotelDetails.rooms ?? []} />
                </section>

                <section
                    id={HOTEL_BASIC_INFO_ID}
                    className={classNames(classes.mainLayout, classes.mainLayoutMod)}
                >
                    <HotelInfo />
                </section>

                <section id={HOTEL_MAP_ELEM_ID} className={classes.dark}>
                    <div className={classNames(classes.mainLayout, classes.mainLayoutMod)}>
                        <HotelOnMap />
                    </div>
                </section>

                <section id={HOTEL_REVIEWS_LIST_ELEM_ID}>
                    <HotelReviews />
                </section>

            </main>
        );
    }

    private getActualNavLinks = (): NavigationMenuItem[] => DEFAULT_NAVIGATION_ITEMS.slice();
    
    // tslint:disable:member-ordering
    private resizeHandler = debounce(() => {
        const scrollWidth = getScrollWidth();
        const type = getAdaptiveType(scrollWidth);
        if (type !== S.ui.adaptiveType) {
            S.ui.setAdaptiveType(type);
        }

        const searchFormAdaptiveType = getSearchFormAdaptiveType(scrollWidth);
        if (searchFormAdaptiveType !== S.ui.searchFormAdaptiveType) {
            S.ui.setSearchFormAdaptiveType(searchFormAdaptiveType);
        }

        const postFiltersType = getPostFiltersAdaptiveType(scrollWidth);
        if (postFiltersType !== S.ui.postFiltersAdaptiveType) {
            S.ui.setPostFiltersAdaptiveType(postFiltersType);
        }

        const hotelRoomsAdaptiveType = getHotelRoomsAdaptiveType(scrollWidth);
        if (hotelRoomsAdaptiveType !== S.ui.hotelRoomsAdaptiveType) {
            S.ui.setHotelRoomsAdaptiveType(hotelRoomsAdaptiveType);
        }

        const reviewsType = getReviewsAdaptiveType(scrollWidth);
        if (reviewsType !== S.hotelReviews.adaptiveView) {
            S.hotelReviews.adaptiveView = reviewsType;
        }
    }, 100);
    // tslint:enable:member-ordering
}
